<template>
  <ProductListingPageLayout :breadcrumb="data.breadcrumb" :is-empty="isEmpty" :filters-applied="filters.length > 0">
    <template #breadcrumbs>
      <CBreadcrumbs :breadcrumb="data.breadcrumb" base-path="/kategorier/" />
    </template>

    <template v-if="hero" #category-hero>
      <TheHero
        data-test="product-category-hero"
        :title="hero.title"
        :title-font-size="hero.titleFontSize"
        :background-url-desktop="heroUrlDesktop"
        :background-url-mobile="heroUrlMobile"
        :overlay="hero.overlay"
      />
    </template>

    <template v-if="data.breadcrumb" #title-desc>
      <CHeading v-if="!hero" class="text-primary-900" :level="1" :display="3" data-test="category-name">
        {{ data.breadcrumb.name }}
      </CHeading>
      <ProductListingPageDescription
        v-if="categoryDescription"
        data-test="product-category-description"
        :contentful-description="categoryDescription"
      />
    </template>

    <template #empty>
      <div
        class="flex flex-col justify-center max-w-xl text-center gap-y-4"
        :class="[filters.length ? 'pt-16' : 'pt-14']"
      >
        <template v-if="!filters.length">
          <CHeading class="text-primary-900" :level="2">
            {{ t('no_products_to_show') }}
          </CHeading>
          <CText variant="md">
            {{ t('no_products_reason') }}
          </CText>
          <CLink
            class="flex justify-center"
            variant="dark"
            size="medium"
            :to="!!getParentCategory ? `/kategorier/${getParentCategory.slug}` : '/'"
          >
            {{
              !!getParentCategory
                ? t('back_to_category', {
                    category: getParentCategory.name,
                  })
                : t('back_to_homepage')
            }}
          </CLink>
        </template>
        <template v-else>
          <CHeading class="text-primary-900" :level="2"> {{ t('no_result_found') }}</CHeading>
          <CText>
            {{ t('try_another_filter_or_choose_another_delivery_method') }}
          </CText>
        </template>
      </div>
    </template>

    <template v-if="!isEmpty || (isEmpty && filters.length > 0)" #count>
      <span class="overflow-hidden whitespace-nowrap text-ellipsis">
        {{ t('number_of_variants') }}:
        <span class="font-bold" data-test="count">{{ data.totalVariantHits }}</span>
      </span>
    </template>

    <template v-if="!isEmpty || (isEmpty && filters.length > 0)" #sort>
      <SortProducts :sort-order="sortOrder" @change-sorting="changeSorting($event)" />
    </template>

    <template v-if="!isEmpty" #per-page>
      <CDropdown
        :options="productsPerPageOptions"
        :value="String(productsPerPage)"
        label="Antal per sida"
        @update:model-value="updateProductsPerPage"
      />
    </template>

    <template #products>
      <ProductTileWrapper
        v-for="product in currentProducts"
        :key="`${product.id} ${product.name}`"
        :ref="(el) => (productTiles[product.id] = el)"
        :product="product"
        :badges="product.badges"
        :is-variants-toggled="currentExpandedProduct === product.id"
        @variants-toggled="updateCurrentExpandedProduct(product.id, $event)"
      />
      <div v-if="pending">
        <ProductSkeletonTile v-for="index in productsPerPage" :key="index" />
      </div>
      <div v-else-if="data.totalProductHits > 0" class="flex w-full mb-6">
        <CPagination :active="activePage" :pages="pages" @update:model-value="updateActivePage" />
      </div>
    </template>
  </ProductListingPageLayout>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import * as contentfulRichText from '@contentful/rich-text-types';
import type { Breadcrumb } from '@/types/page';
import { DELIVERY_EXPRESS, EXPRESS_DELIVERIES } from '@/config/delivery-config';
import {
  useProductStore,
  useCampaignStore,
  useDeliveryStore,
  useUserStore,
  useAssortmentStore,
  useBreadcrumbStore,
  useTrackingStore,
  useBrandStore,
} from '@/stores';
import { Product } from '@/types/products';
import { productsPerPageOptions, sortOptions } from '@/constants';

const { Block, Inline } = contentfulRichText;

defineRouteRules({
  swr: 60 * 10,
  cache: {
    base: 'cache-storage',
  },
});

type ContentFields = {
  categoryText: typeof Block | typeof Inline;
  hero: {
    fields: any;
  };
} | null;

type Data = {
  breadcrumb: Breadcrumb;
  contentFields: ContentFields;
  totalVariantHits: number;
  totalProductHits: number;
  currentProductCount: number;
  products: Product[];
};

const defaultData = {
  breadcrumb: { name: '', parents: [] },
  contentFields: null,
  totalVariantHits: 0,
  totalProductHits: 0,
  currentProductCount: 0,
  products: [],
};

const route = useRoute();
const router = useRouter();
const { $sentry, $searchService, $tracking } = useNuxtApp();
const { params, name, fullPath, query } = route;
const { public: config } = useRuntimeConfig();
const { t } = useI18n();
const { fetchEntry } = useContentful();

const { brandConfig } = useBrandStore();
const productStore = useProductStore();
const userStore = useUserStore();
const deliveryStore = useDeliveryStore();

const { resetProductHistory, setFacetsFromSearchResult, pushProductHistory, clearFilters, clearSequenceData } =
  productStore;
const { registerSessionStartedHook, unregisterSessionStartedHook } = userStore;
const { getBreadcrumb } = useBreadcrumbStore();

const { getSelectedDeliveryId, getCurrentStockLocation, getCurrentStockLocations, selectedLocations, rawLocations } =
  storeToRefs(deliveryStore);
const { assortmentId } = storeToRefs(useAssortmentStore());
const { isLoggedIn } = storeToRefs(userStore);
const { trackingId } = storeToRefs(useTrackingStore());
const { campaigns } = storeToRefs(useCampaignStore());
const { productHistory, languageConfig } = storeToRefs(productStore);

const currentProducts = ref<Product[]>([]);
const currentExpandedProduct = ref<any>(undefined);
const isInitialized = ref(false);
const filters = ref(createFilterList(query));
const productTiles = ref<any>({});

const activePage = computed(() => {
  const page = Number(route.query.page);
  return page > 0 ? page : 1;
});

const startIndex = computed(() => (activePage.value - 1) * productsPerPage.value);

const productsPerPage = computed(() => {
  const perPage = Number(route.query.perPage);
  return perPage > 0 ? perPage : 20;
});

const sortOrder = computed(() => {
  const sortQuery = route.query.sort as string;
  return sortOptions.includes(sortQuery) ? sortQuery : undefined;
});

const slug = computed(() => (params ? transformPlpSlug(params.slug) : ''));
const isExpressDelivery = computed(() => isLoggedIn.value && EXPRESS_DELIVERIES.includes(getSelectedDeliveryId.value));
const pages = computed(() => {
  const totalProducts = data.value.totalProductHits;

  return Math.ceil(totalProducts / productsPerPage.value);
});

const productImpression = (products: Product[], side?: string) => {
  if (process.server) {
    return;
  }

  $tracking.emit('productImpression', {
    products,
    list: config.gtm.PRODUCT_LIST,
    side,
  });
};

const getBadgesByCampaignIds = (campaignIds: string[]) => {
  if (campaignIds.length) {
    return getBadges(campaigns.value, campaignIds);
  }
  return [];
};

const enrichProducts = () => {
  currentProducts.value = currentProducts.value.map((p: Product) => {
    const variantCampaignIds = [...new Set(p.variants.flatMap((variant) => variant.campaignIds))];
    return { ...p, badges: getBadgesByCampaignIds(variantCampaignIds) };
  });
};

const loadProducts = async () => {
  let products: Product[] = [];
  try {
    const currentFilters = [...filters.value];
    const sortBy = [];
    const additionalFilters = [];

    if (isExpressDelivery.value) {
      additionalFilters.push({
        attributeName: 'stockedAt',
        name: 'stockedAt',
        selected: [getCurrentStockLocation.value],
        type: 'distinct',
      });

      if (getSelectedDeliveryId.value === DELIVERY_EXPRESS) {
        additionalFilters.push({
          attributeName: 'filterTags',
          name: 'filterTags',
          selected: ['express-delivery'],
          type: 'distinct',
        });
      }
    } else if (brandConfig.additions.useStockedAtForStockedFilter) {
      if (!process.server) {
        const index = currentFilters.findIndex((filter) => filter.name === STOCKED_AT_NAME);
        if (index !== -1) {
          currentFilters.splice(index, 1);
          additionalFilters.push({
            attributeName: 'stockedAt',
            name: STOCKED_AT_NAME,
            selected: getRawLocations.value,
            type: 'distinct',
          });
        }
      }
    }

    if (sortOrder.value) {
      if (sortOrder.value === 'rel') {
        sortBy.push({
          type: 'relevance',
        });
      } else {
        const sortingParameter = {
          type: 'attribute',
          attributeName: 'name',
          order: sortOrder.value,
        };

        sortBy.push(sortingParameter);
      }
    }

    const body = {
      attribute: {
        name: 'splitCategoryPaths',
        value: slug.value,
      },
      resultsOptions: {
        skip: startIndex.value,
        take: productsPerPage.value,
        sortBy,
        facets: [...currentFilters, ...additionalFilters],
      },
    };

    const fetchedResults = await getProductsByAttribute(
      $searchService,
      brandConfig,
      { body },
      {
        trackingId: trackingId.value,
        assortmentId: assortmentId.value ?? 'default',
        languageConfig: languageConfig.value,
      },
    );
    const { facets, facetsData, side } = fetchedResults;

    setFacetsFromSearchResult({ facets, facetsData });
    let totalVariantHits = 0;
    let totalProductHits = 0;
    let currentProductCount = 0;
    if (!fetchedResults?.products?.length) {
      totalVariantHits = totalProductHits = currentProductCount = 0;
    } else {
      const { products: fetchedProducts, count, VariantResults_TotalItems: variantResultTotalItems } = fetchedResults;
      totalProductHits = count;
      totalVariantHits = variantResultTotalItems;
      products = fetchedProducts;
      currentProductCount = products.length;

      pushProductHistory({
        route: {
          name,
          path: fullPath,
          query: route.query,
        },
      });

      productImpression(
        fetchedProducts.map((product: Product, index: number) => {
          return {
            ...product,
            listPosition: currentProductCount - products.length + index + 1,
          };
        }),
        side,
      );
    }

    return { totalVariantHits, totalProductHits, currentProductCount, products };
  } catch (error: any) {
    $sentry.captureException(error);
    if (error?.response?.status === 400) {
      createError({ statusCode: 404, message: 'Failed to get products' });
    } else {
      createError({ statusCode: 500, message: 'Failed to get products' });
    }
    return { totalVariantHits: 0, totalProductHits: 0, currentProductCount: 0, products: [] };
  }
};

const { data, pending, refresh } = await useAsyncData(
  async (): Promise<Data> => {
    if (!slug.value) {
      $sentry.captureException(new Error('Missing value for property slug'));
      throw showError({ statusCode: 404, message: 'No category slug provided' });
    }
    const productPromise = loadProducts();
    const breadcrumbPromise = getBreadcrumb({ slug: slug.value });
    const [breadcrumb, { totalVariantHits, totalProductHits, currentProductCount, products }] = await Promise.all([
      breadcrumbPromise,
      productPromise,
    ]);

    // Non-existing categories have empty name in breadcrumbs
    const isExistingCategory = !!breadcrumb.name;
    if (!isExistingCategory) {
      throw showError({ statusCode: 404, message: 'Category does not exist' });
    }

    const categorySlug = breadcrumb.slug;
    let contentFields;
    if (categorySlug) {
      const entry = await fetchEntry(
        {
          contentType: 'productListingPage',
          'fields.slug': `${categorySlug}`,
        },
        { reportErrors: false },
      );
      contentFields = entry.data?.fields;
    }

    return { breadcrumb, contentFields, totalVariantHits, totalProductHits, currentProductCount, products };
  },
  {
    default: () => defaultData,
    lazy: true,
  },
);

const initRefresh = () => {
  currentProducts.value = [];
  refresh();
};

const attributesToHide = computed(() => []);
provide('attributesToHide', attributesToHide.value);

const title = computed(() => {
  const brandTitle = t('head.plp_pdp_title_suffix');
  if (!data.value.breadcrumb.name) {
    return brandTitle;
  }
  return `${data.value.breadcrumb.name} - ${brandTitle}`;
});

const categoryDescription = computed(() => data.value.contentFields?.categoryText);
const hero = computed(() => !data.value.breadcrumb.parents?.length && data.value.contentFields?.hero?.fields);
const heroUrlDesktop = computed(() => hero.value?.heroDesktop?.fields?.file?.url);
const heroUrlMobile = computed(() => hero.value?.heroMobile?.fields?.file?.url);

const getParentCategory = computed(() => data.value.breadcrumb.parents?.[data.value.breadcrumb.parents.length - 1]);
const getRawLocations = computed(() => {
  const locations = rawLocations.value[getSelectedDeliveryId.value];
  return locations.length ? locations : getCurrentStockLocations.value;
});

const scrollToProduct = (productId: string, behavior?: ScrollBehavior) => {
  const position = productTiles.value[productId]?.$el.offsetTop;
  const SCROLL_POS_PADDING = 50;
  if (position) window.scrollTo({ top: position - SCROLL_POS_PADDING, behavior });
};

const isEmpty = computed(() => data.value.currentProductCount === 0 && !pending.value);

const initFunc = () => {
  isInitialized.value = true;
  initRefresh();
};

const updateCurrentExpandedProduct = (id: string, toggled: boolean) => {
  currentExpandedProduct.value = undefined;
  if (!toggled) return;
  currentExpandedProduct.value = id;
  nextTick(() => {
    scrollToProduct(id, 'smooth');
  });
};

const loadBreadcrumbs = async () => {
  try {
    if (data.value?.breadcrumb) {
      data.value.breadcrumb = await getBreadcrumb({
        slug: slug.value,
        assortmentId: assortmentId.value || 'default',
      });
    }
  } catch (error) {
    $sentry.captureException(error);
  }
};

const changeSorting = (newSortOrder: string) => {
  router.push({ path: route.path, query: { ...route.query, sort: newSortOrder } });
};

const updateProductsPerPage = (value: string) => {
  router.push({ path: route.path, query: { ...route.query, perPage: value, page: undefined } });
};

const updateActivePage = (newPage: number) => {
  if (newPage === activePage.value) return;

  router.push({ path: route.path, query: { ...route.query, page: newPage } });

  const firstProductId = currentProducts.value[0]?.id;

  resetProductHistory();

  if (!firstProductId) return;

  scrollToProduct(firstProductId, 'smooth');
};

watch(pending, (pending) => {
  const prevVisitedProduct = productHistory.value.productId;
  if (!pending && prevVisitedProduct) scrollToProduct(prevVisitedProduct);
});

watch(
  () => data.value.products,
  (products) => {
    currentProducts.value = products;
    enrichProducts();
  },
  { immediate: true },
);

watch(campaigns, () => {
  enrichProducts();
});

watch(productsPerPage, () => {
  initRefresh();
});

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    const { page: _newPage, sort: _newSort, ...newQueryCleaned } = newQuery;
    const { page: _oldPage, sort: _oldSort, ...oldQueryClearned } = oldQuery;

    const filtersDiffer = JSON.stringify(newQueryCleaned) !== JSON.stringify(oldQueryClearned);

    if (
      (oldQuery.page !== newQuery.page || oldQuery.perPage !== newQuery.perPage || oldQuery.sort !== newQuery.sort) &&
      !filtersDiffer
    ) {
      resetProductHistory();
      initRefresh();
      return;
    }

    if (oldQuery.page && newQuery.page) {
      if (filtersDiffer) {
        router.push({ query: newQueryCleaned });
      }
    } else {
      router.push({ query: newQueryCleaned });
    }

    filters.value = createFilterList(route.query);
    data.value.currentProductCount = 0;
    resetProductHistory();
    if (slug.value) {
      loadBreadcrumbs();
      initRefresh();
    } else {
      $sentry.captureException(new Error('Missing value for property slug'));
    }
  },
);

watch(assortmentId, (newId, oldId) => {
  if (!isInitialized.value) {
    return;
  }
  const normalizedNewId = newId || 'default';
  const normalizedOldId = oldId || 'default';
  if (normalizedNewId !== normalizedOldId) {
    loadBreadcrumbs();
    initRefresh();
  }
});

watch(getSelectedDeliveryId, (toId, fromId) => {
  if (!isInitialized.value) {
    return;
  }
  if (!brandConfig.centralStorageId) {
    initRefresh();
  } else {
    if (isExpressDelivery.value && !getCurrentStockLocation.value) return;
    if (EXPRESS_DELIVERIES.includes(toId) || EXPRESS_DELIVERIES.includes(fromId)) {
      initRefresh();
    }
  }
});

watch(selectedLocations, (to, from) => {
  if (!isInitialized.value) {
    return;
  }
  if (!brandConfig.centralStorageId) {
    initRefresh();
  } else {
    const currentExpressOptionChangedStore =
      isExpressDelivery.value && to[getSelectedDeliveryId.value] !== from[getSelectedDeliveryId.value];

    if (currentExpressOptionChangedStore) {
      initRefresh();
    }
  }
});

const getCanonicalCategoryPath = (fullPath: string): string => {
  return fullPath.split('?')[0];
};

const getMeta = () => {
  const meta = [];
  if (categoryDescription.value) {
    meta.push({
      hid: 'description',
      name: 'description',
      content: documentToPlainTextString(categoryDescription.value),
    });
  }
  return {
    title: title.value,
    meta,
    link: [
      {
        rel: 'canonical',
        href: `${config.baseUrl}${getCanonicalCategoryPath(fullPath)}`,
      },
    ],
  };
};
useHead(getMeta());

onMounted(() => {
  if (data.value.products.length) {
    productImpression(data.value.products);
  }
  if (!isLoggedIn.value) {
    registerSessionStartedHook(initFunc);
  } else {
    isInitialized.value = true;
  }
});

onBeforeUnmount(() => {
  clearFilters();
  clearSequenceData();
  unregisterSessionStartedHook(initFunc);
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductListingPage',
});
</script>
